import React, { useState, useEffect } from 'react';
import LoadingState from '../../components/common/LoadingState';
import ErrorAlert from '../../components/common/ErrorAlert';
import EmptyState from '../../components/common/EmptyState';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';
import { simpleApi, Warehouse as WarehouseType, WarehouseCreate } from '../../services/simpleApi';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  alpha,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  Add,
  Warehouse as WarehouseIcon,
  LocalShipping,
  Inventory,
  TrendingUp,
  Edit,
  Delete,
  CheckCircle,
  Warning,
} from '@mui/icons-material';

interface Location {
  id: string;
  name: string;
  type: 'warehouse' | 'distribution_center' | 'retail';
  address: string;
  city: string;
  country: string;
  status: 'operational' | 'maintenance' | 'inactive';
  capacity: number;
  currentUtilization: number;
  itemCount: number;
  manager: string;
  contact: string;
}

const LocationsPage: React.FC = () => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState<WarehouseCreate>({ name: '' });

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await simpleApi.warehouses.list();
      // Map warehouses to locations
      const items: Location[] = response.items.map((w: WarehouseType) => ({
        id: w.id,
        name: w.name,
        type: (w.warehouse_type as any) || 'warehouse',
        address: w.address || '',
        city: w.city || '',
        country: w.country || '',
        status: w.status === 'active' ? 'operational' : w.status === 'inactive' ? 'inactive' : 'maintenance',
        capacity: w.total_capacity,
        currentUtilization: w.current_utilization,
        itemCount: Math.floor(w.current_utilization * 100),
        manager: w.manager_name || 'Not Assigned',
        contact: w.contact_email || w.contact_phone || '',
      }));
      setLocations(items);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingLocation) {
        await simpleApi.warehouses.update(editingLocation.id, formData);
        setSuccess('Location updated successfully');
      } else {
        await simpleApi.warehouses.create(formData);
        setSuccess('Location created successfully');
      }
      setOpenDialog(false);
      fetchItems();
    } catch (err: any) {
      setError(err.message || 'Failed to save location');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await simpleApi.warehouses.delete(id);
      setSuccess('Location deleted successfully');
      fetchItems();
    } catch (err: any) {
      setError(err.message || 'Failed to delete location');
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const clearSuccess = () => setSuccess(null);

  // Mock data removed - using API

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational': return 'success';
      case 'maintenance': return 'warning';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational': return <CheckCircle />;
      case 'maintenance': return <Warning />;
      default: return undefined;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'warehouse': return <WarehouseIcon />;
      case 'distribution_center': return <LocalShipping />;
      case 'retail': return <Inventory />;
      default: return <WarehouseIcon />;
    }
  };

  const getUtilizationColor = (utilization: number, capacity: number) => {
    const percentage = (utilization / capacity) * 100;
    if (percentage >= 90) return theme.palette.error.main;
    if (percentage >= 75) return theme.palette.warning.main;
    return theme.palette.success.main;
  };

  const totalCapacity = (locations || []).reduce((sum, loc) => sum + loc.capacity, 0);
  const totalUtilization = (locations || []).reduce((sum, loc) => sum + loc.currentUtilization, 0);
  const avgUtilization = totalCapacity > 0 ? (totalUtilization / totalCapacity) * 100 : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Locations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage warehouses and distribution centers
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingLocation(null);
            setOpenDialog(true);
          }}
        >
          Add Location
        </Button>
      </Box>

      {error && <ErrorAlert error={error} onRetry={fetchItems} />}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                {locations.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Locations
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.success.main }}>
                {locations.filter(l => l.status === 'operational').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operational
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
                {totalCapacity.toLocaleString()} m²
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Capacity
              </Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <Box sx={{ p: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.info.main }}>
                {avgUtilization.toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Avg Utilization
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Locations Grid */}
      {loading ? (
        <LoadingState message="Loading locations..." />
      ) : (locations || []).length === 0 ? (
        <EmptyState
          title="No locations found"
          description="Add your first warehouse or distribution center"
          actionLabel="Add Location"
          onAction={() => setOpenDialog(true)}
          icon={<WarehouseIcon sx={{ fontSize: 64, color: 'text.disabled' }} />}
        />
      ) : (
      <Grid container spacing={3}>
        {(locations || []).map((location) => {
          const utilizationPercentage = (location.currentUtilization / location.capacity) * 100;
          const utilizationColor = getUtilizationColor(location.currentUtilization, location.capacity);
          
          return (
            <Grid item xs={12} md={6} key={location.id}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <Box sx={{ p: 3 }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      >
                        {getTypeIcon(location.type)}
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {location.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {location.type.replace('_', ' ').toUpperCase()}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingLocation(location);
                          setOpenDialog(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <Delete />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Status */}
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      icon={getStatusIcon(location.status)}
                      label={location.status.toUpperCase()}
                      size="small"
                      color={getStatusColor(location.status)}
                    />
                  </Box>

                  {/* Address */}
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {location.address}<br />
                    {location.city}, {location.country}
                  </Typography>

                  {/* Metrics */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Capacity
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {location.capacity.toLocaleString()} m²
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Items Stored
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {location.itemCount.toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Utilization */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Utilization
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        {utilizationPercentage.toFixed(0)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={utilizationPercentage}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: alpha(utilizationColor, 0.1),
                        '& .MuiLinearProgress-bar': {
                          bgcolor: utilizationColor,
                        },
                      }}
                    />
                  </Box>

                  {/* Manager Info */}
                  <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary">
                      Manager
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {location.manager}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {location.contact}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      )}

      <SuccessSnackbar
        open={!!success}
        message={success || ''}
        onClose={clearSuccess}
      />

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLocation ? 'Edit Location' : 'Add New Location'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Location Name"
                defaultValue={editingLocation?.name}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select label="Type" defaultValue={editingLocation?.type || ''}>
                  <MenuItem value="warehouse">Warehouse</MenuItem>
                  <MenuItem value="distribution_center">Distribution Center</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                defaultValue={editingLocation?.address}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                defaultValue={editingLocation?.city}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                defaultValue={editingLocation?.country}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Capacity (m²)"
                type="number"
                defaultValue={editingLocation?.capacity}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" defaultValue={editingLocation?.status || 'operational'}>
                  <MenuItem value="operational">Operational</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manager"
                defaultValue={editingLocation?.manager}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact"
                defaultValue={editingLocation?.contact}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setOpenDialog(false)}>
            {editingLocation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationsPage;
